import NextImage, { ImageProps as NextImageProps } from 'next/image';
import type { ImgHTMLAttributes } from 'react';
import cn from 'classnames';
import { buildImageFetchSrcSet } from 'helpers/cdn';

import styles from './Image.module.scss';

const EMPTY_BLUR =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

interface ImageProps extends NextImageProps {
  /**
   * Render a real CDN `<img>` that absolutely fills its container instead of
   * the next/image kludge. Only effective when `src` is a plain string URL;
   * `StaticImport` sources silently fall back to the next/image path.
   * Requires a container with explicit dimensions — the `<img>` contributes
   * no height. Opt-in for SSR/SEO cards only; the default kludge lets the
   * image give its container intrinsic height.
   */
  ssrFill?: boolean;
  /**
   * ssrFill only: resized variants for the browser via srcSet, while `src`
   * stays the original URL that og:image and structured data point at.
   */
  ssrWidths?: number[];
}

const Image = ({
  className,
  layout = 'fill',
  placeholder,
  blurDataURL,
  priority,
  alt = '',
  title,
  src,
  ssrFill = false,
  ssrWidths,
  sizes,
  ...rest
}: ImageProps) => {
  if (!src) {
    return null;
  }

  const srcString = typeof src === 'string' ? src : '';

  // SSR-friendly fill images: next/image layout="fill" emits a 1×1 GIF as the
  // main src (real URL only in <noscript>), so crawlers/HTML previews see empty
  // cards. With ssrFill, we emit a real CDN <img> instead.
  //
  // Trade-off: skips /_next/image resize/srcset/WebP. That is intentional for
  // SEO list cards. `priority` only toggles eager + fetchPriority=high vs lazy.
  if (ssrFill && layout === 'fill' && placeholder !== 'blur' && srcString) {
    return (
      <div className={cn(styles.root, className)}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          className={styles.fillImage}
          src={srcString}
          srcSet={
            ssrWidths ? buildImageFetchSrcSet(srcString, ssrWidths) : undefined
          }
          sizes={ssrWidths ? sizes : undefined}
          alt={alt}
          title={typeof title === 'string' ? title : undefined}
          decoding="async"
          loading={priority ? 'eager' : 'lazy'}
          // React DOM types in this repo predate fetchPriority; the attribute
          // is supported by modern browsers and matches next/image priority.
          {...(priority
            ? ({
                fetchPriority: 'high',
              } as ImgHTMLAttributes<HTMLImageElement>)
            : {})}
        />
      </div>
    );
  }

  const blurProps =
    placeholder === 'blur'
      ? {
          placeholder: 'blur' as const,
          blurDataURL: blurDataURL ?? EMPTY_BLUR,
        }
      : {};

  // kludge to create a responsive image
  // https://github.com/vercel/next.js/discussions/18739#discussioncomment-344932
  if (layout === 'fill') {
    return (
      <div className={cn(styles.root, className)}>
        <NextImage
          className={styles.image}
          objectFit="cover"
          objectPosition="center"
          layout={layout}
          priority={priority}
          alt={alt}
          title={title}
          src={src}
          sizes={sizes}
          {...blurProps}
          {...rest}
        />
      </div>
    );
  }

  return (
    <NextImage
      layout={layout}
      className={className}
      priority={priority}
      alt={alt}
      title={title}
      src={src}
      sizes={sizes}
      {...blurProps}
      {...rest}
    />
  );
};

export default Image;
